import { FilePdfOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { monoFont, tabularNums } from '@medcom/shared-ui/styles/globalCss.ts';
import { antdTokenFilter } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { App, Button, Flex, Form, Select, Switch, Typography } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { useObjectUrlResource } from '../../hooks/useObjectUrlResource';
import { PdfPrintPreview } from '../../PdfPrintPreview.tsx';
import {
  API_DATETIME_FORMAT,
  orderReportRangeFileNamePart,
  orderReportRangeLabel,
  orderReportSnapshotFileNamePart,
  orderReportSnapshotLabel,
  SHORT_DATE_FORMAT,
  SHORT_DATETIME_FORMAT,
} from '../../utils/dateFormatUtils';
import { patientFullName } from '../../utils/patient.ts';
import { DateRangeSelect } from './DateRangeSelect.tsx';
import { ORDER_REPORT_QUERY } from './OrderReport.gql';

// The picker only offers minutes, so "bis 15:30" has to include that whole minute.
const inclusiveRange = (range: [dayjs.Dayjs, dayjs.Dayjs]): [dayjs.Dayjs, dayjs.Dayjs] => [range[0], range[1].second(59)];

type FormModel = {
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  orderIds: string[];
  labId: string;
  showParams: boolean;
  showAddress: boolean;
  open: boolean;
  sortBy: 'REAL_CREATED_AT' | 'PATIENT_LAST_NAME';
};

export const OrderReport: React.FC<{ doctorId: string }> = ({ doctorId }) => {
  const [form] = useForm<FormModel>();
  const labId = useWatch('labId', form);
  const open = useWatch('open', form);
  const dateRange = useWatch('dateRange', form) || [dayjs().startOf('day'), dayjs().endOf('day')];
  const [generatedAt, setGeneratedAt] = useState(dayjs());
  const { message } = App.useApp();

  const { data, loading } = useQuery(ORDER_REPORT_QUERY, {
    variables: {
      doctorId: doctorId,
    },
  });

  const reportLabs = useMemo(() => {
    const allowedLabIds = new Set(data?.labs?.map(it => it.id));
    return data?.doctor?.assignedLabs.filter(it => allowedLabIds.has(it.lab.id)) ?? [];
  }, [data]);

  const {
    objectUrl: pdfUrl,
    blob: pdfBlob,
    fetchAndSet: fetchPdf,
    cleanup: cleanupPdf,
    loading: pdfLoading,
  } = useObjectUrlResource(() => message.error('Es ist ein Fehler beim Laden des Dokuments aufgetreten'), 'application/pdf');

  useEffect(() => {
    if (reportLabs.length) {
      form.setFieldValue('labId', reportLabs[0].lab.id);
    }
  }, [form, reportLabs]);

  const generate = async (values: FormModel) => {
    const [from, to] = inclusiveRange(values.dateRange);
    setGeneratedAt(dayjs());
    const params = new URLSearchParams({
      doctorId: doctorId,
      labId: reportLabs.find(it => it.lab.id === values.labId)?.lab.identifier ?? '',
      dateFrom: from.format(API_DATETIME_FORMAT),
      dateTo: to.format(API_DATETIME_FORMAT),
      showParams: values.showParams ? 'true' : 'false',
      showAddress: values.showAddress ? 'true' : 'false',
      open: values.open ? 'true' : 'false',
      sortBy: values.sortBy,
    });

    // Add each orderId as its own param
    // This can result in a long string which potentially needs refactoring if we exceed the max URL length
    values.orderIds?.map(id => params.append('orderIds', id));

    const url = `${window._env_.API_URL}/rest/order-report?${params.toString()}`;
    fetchPdf(url);
  };

  const download = () => {
    if (!pdfBlob) {
      return;
    }

    let name = 'gesendete-auftraege';
    if (open) {
      name = 'offene-auftraege';
    }
    const labName = reportLabs.find(it => it.lab.id === labId)?.lab.shortName;

    const datePart = open ? orderReportSnapshotFileNamePart(generatedAt) : orderReportRangeFileNamePart(...inclusiveRange(dateRange));

    saveAs(pdfBlob, `${name}_${labName}_${datePart}.pdf`);
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  let name = 'Gesendete Aufträge';
  if (open) {
    name = 'Offene Aufträge';
  }
  const labName = reportLabs.find(it => it.lab.id === labId)?.lab.shortName;
  const rangeLabel = open ? orderReportSnapshotLabel(generatedAt) : orderReportRangeLabel(...inclusiveRange(dateRange));
  const title = `${name} ${labName} ${rangeLabel}`;

  return (
    <>
      <Form<FormModel>
        layout="horizontal"
        colon={false}
        labelCol={{ style: { width: '120px' } }}
        labelAlign="left"
        form={form}
        onFinish={() => generate(form.getFieldsValue(true))}
        initialValues={{
          dateRange: [dayjs().startOf('day'), dayjs().endOf('day')],
          labId: '',
          orderIds: [],
          showParams: false,
          showAddress: false,
          open: false,
          sortBy: 'REAL_CREATED_AT',
        }}
      >
        <Form.Item name="open" label="Offene Aufträge">
          <Switch />
        </Form.Item>
        <Form.Item name="labId" label="Labor" hidden={reportLabs.length <= 1}>
          <Select
            options={reportLabs.map(it => ({
              label: it.lab.name,
              value: it.lab.id,
            }))}
            popupMatchSelectWidth={false}
            style={{ width: '100%' }}
            allowClear={false}
            onChange={() => form.setFieldValue('orderIds', [])}
          />
        </Form.Item>
        {open ? (
          <Form.Item
            name="orderIds"
            label="Aufträge"
            tooltip="Suche nach Name, SVNR, Geburtstag, Erstellungszeitpunkt, Kommentar und Auftragsnummer möglich. Wenn keine Aufträge ausgewählt sind, werden automatisch alle offenen Aufträge verwendet."
          >
            <Select
              allowClear
              showSearch={{ filterOption: antdTokenFilter }}
              mode="multiple"
              placeholder="Alle offenen Aufträge"
              maxTagCount="responsive"
              optionLabelProp="taglabel"
              popupMatchSelectWidth={false}
              options={
                data?.openRequests
                  ?.flatMap(it => it.orders)
                  ?.filter(it => it.lab.id === labId)
                  ?.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                  ?.map(it => {
                    const patientName = patientFullName(it.request.patientData);
                    const svnr = it.request.patientData.svnr;
                    const birthday = dayjs(it.request.patientData.birthday).format(SHORT_DATE_FORMAT);
                    const createdAt = dayjs(it.createdAt).format(SHORT_DATETIME_FORMAT);
                    const comment = it.request.comment;

                    return {
                      taglabel: it.request.patientData.lastName,
                      search: `${patientName} ${svnr} ${birthday} ${createdAt} ${comment} ${it.number}`,
                      label: (
                        <>
                          <div>{patientName}</div>
                          <div>
                            <Typography.Text type="secondary">
                              {svnr ? (
                                <>
                                  {'SVNR: '}
                                  <span className={monoFont}>{svnr}</span>
                                  {', '}
                                </>
                              ) : null}
                              {'Geb: '}
                              <span className={tabularNums}>{birthday}</span>
                              {', Erstellt: '}
                              <span className={tabularNums}>{createdAt}</span>
                              {', Auftrag: '}
                              <span className={monoFont}>{it.number}</span>
                            </Typography.Text>
                          </div>
                          {comment && (
                            <div>
                              <Typography.Text type="secondary">{comment}</Typography.Text>
                            </div>
                          )}
                        </>
                      ),
                      value: it.id,
                    };
                  }) ?? []
              }
            />
          </Form.Item>
        ) : (
          <Form.Item
            name="dateRange"
            label="Zeitraum"
            tooltip="Maßgeblich ist der Sendezeitpunkt der Aufträge, nicht der Erstellungszeitpunkt."
          >
            <DateRangeSelect />
          </Form.Item>
        )}
        <Form.Item name="sortBy" label="Sortierung">
          <Select
            popupMatchSelectWidth={false}
            options={[
              { value: 'REAL_CREATED_AT', label: 'Auftrag Erstellungszeitpunkt' },
              { value: 'PATIENT_LAST_NAME', label: 'Patient Nachname' },
            ]}
            style={{ width: '100%' }}
            allowClear={false}
          />
        </Form.Item>
        <Flex gap="middle" wrap>
          <Form.Item name="showParams">
            <Switch checkedChildren="Mit Parameter" unCheckedChildren="Ohne Parameter" />
          </Form.Item>
          <Form.Item name="showAddress">
            <Switch checkedChildren="Mit Adresse" unCheckedChildren="Ohne Adresse" />
          </Form.Item>
        </Flex>
        <Button htmlType="submit" icon={<FilePdfOutlined />} style={{ width: '100%' }} disabled={!labId}>
          Erstellen
        </Button>
      </Form>
      <PdfPrintPreview title={title} url={pdfUrl} loading={pdfLoading} blob={pdfBlob} onDownload={download} onClose={cleanupPdf} />
    </>
  );
};
